"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import type * as React from "react";
import { type ChevronProps, DayPicker } from "react-day-picker";

import { variants } from "../button/config";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	...props
}: CalendarProps) {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={`p-3 ${className}`}
			classNames={{
				months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
				month: "space-y-4",
				month_caption: "flex justify-center pt-1 relative items-center",
				caption_label: "text-sm font-medium",
				nav: "space-x-1 flex items-center",
				button_previous: `${variants.outline} absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100`,
				button_next: `${variants.outline} absolute right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100`,
				month_grid: "w-full border-collapse space-y-1",
				weekdays: "flex",
				weekday:
					"text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
				week: "flex w-full mt-2",
				day: `relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md ${
					props.mode === "range"
						? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
						: "[&:has([aria-selected])]:rounded-md"
				}`,

				day_button: `${variants.ghost} h-8 w-8 p-0 font-normal aria-selected:opacity-100`,
				range_start: "day-range-start",
				range_end: "day-range-end",
				selected:
					"bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
				today: "bg-accent text-accent-foreground",
				outside:
					"day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
				disabled: "text-muted-foreground opacity-50",
				range_middle:
					"aria-selected:bg-accent aria-selected:text-accent-foreground",
				hidden: "invisible",
				...classNames,
			}}
			components={{
				Chevron: ({ className, orientation, ...props }: ChevronProps) =>
					orientation === "left" ? (
						<ChevronLeft
							className={`mx-auto h-4 w-4 ${className}`}
							{...props}
						/>
					) : (
						<ChevronRight
							className={`mx-auto h-4 w-4 ${className}`}
							{...props}
						/>
					),
			}}
			{...props}
		/>
	);
}
Calendar.displayName = "Calendar";

export { Calendar };
